import { Fragment, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Chip, Tooltip } from "@mui/material";
import { getErstelltAmEML } from "../helper.js";
import { Download } from "@mui/icons-material";
import PostalMime from "postal-mime";

EMailViewer.propTypes = {
  document: PropTypes.object,
};

export default function EMailViewer(props) {
  const { document } = props;
  const [mailObject, setMailObject] = useState();
  const downloadLinkRef = useRef(null);

  const blobToBase64 = async (blob) => {
    return new Promise((resolve, _) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  useEffect(() => {
    const getData = async () => {
      const parser = new PostalMime();
      let base64Content = document.filedata;
      if (base64Content.startsWith("data:"))
        base64Content = base64Content.split("base64,")[1];
      const email = await parser.parse(atob(base64Content));

      let html = email.html;

      const attachmentPromises = email.attachments.map(async (attachment) => {
        if (attachment.disposition === "inline") {
          const cid = "cid:" + attachment.contentId.slice(1, -1);
          const base64 = await blobToBase64(
            new Blob([attachment.content], { type: attachment.mimeType })
          );
          html = html.replace(cid, base64);
        }
      });

      await Promise.all(attachmentPromises);
      email.html = html;
      setMailObject(email);
    };
    getData();
  }, []);

  const performDownload = (attachment) => {
    const url = window.URL.createObjectURL(
      new Blob([attachment.content], { type: attachment.mimeType })
    );
    downloadLinkRef.current.href = url;
    downloadLinkRef.current.download = attachment.filename;
    downloadLinkRef.current.click();
    window.URL.revokeObjectURL(url);
  };

  const truncate = (input, maxlength = 20) => {
    if (input && input.length > maxlength) {
      return input.slice(0, maxlength) + "...";
    } else return input;
  };

  const getFrom = (from) => {
    let res = "";
    if (!from) {
      return res;
    }
    if (from.name) {
      res = from.name + " <" + from.address + ">";
    } else {
      res = from.address;
    }

    return res;
  };

  const getTo = (to) => {
    if (!to) {
      return <></>;
    }
    return to.map((recipient, index) => {
      if (recipient.name)
        return (
          <Typography key={index} variant="small" sx={{ pl: "10px" }}>
            <Tooltip title={recipient.address}>
              <a href={"mailto:" + recipient.address}>{recipient.name}</a>
            </Tooltip>
          </Typography>
        );
      else
        return (
          <Typography key={index} variant="small" sx={{ pl: "10px" }}>
            <a href={"mailto:" + recipient.address}>{recipient.address}</a>
          </Typography>
        );
    });
  };

  const EmailBody = () => {
    let htmlBody = mailObject.html;
    if (htmlBody) {
      // converted emails from .msg to .eml often have "position:absolute" in the style,
      // so we replace it with "position:block" to avoid rendering issues
      // this is a workaround and might not be the best solution, but it works for now
      htmlBody = htmlBody.replaceAll("position:absolute", "position:block");
    }

    mailObject.attachments.forEach((attachment) => {
      if (attachment.pidContentId) {
        htmlBody = htmlBody.replace(
          "cid:" + attachment.pidContentId,
          "data:" + attachment.mimeType + ";base64," + attachment.content
        );
      }
    });

    return <Typography dangerouslySetInnerHTML={{ __html: htmlBody }} />;
  };

  return (
    mailObject && (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            pb: "15px",
          }}
        >
          <Typography variant="title">{mailObject.subject}</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="bodybold">
            <a href={"mailto:" + mailObject?.from?.address}>
              {getFrom(mailObject?.from)}
            </a>
          </Typography>
          <Typography variant="body">
            {getErstelltAmEML(mailObject.date)}
          </Typography>
        </Box>
        <Box
          sx={{ display: "flex", flexDirection: "row", justifyContent: "left" }}
        >
          <Typography variant="small">An:</Typography>
          {getTo(mailObject.to)}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "left",
            flexWrap: "wrap",
            gap: "10px",
            pb: "15px",
            pt: "15px",
          }}
        >
          {mailObject.attachments.map((attachment, index) => {
            if (attachment.disposition === "attachment")
              return (
                <Tooltip key={index} title={attachment.fileName}>
                  <Chip
                    key={`attachment_${index}`}
                    onClick={(e) => performDownload(attachment)}
                    sx={{
                      borderColor: "#C0C0C0",
                      borderStyle: "solid",
                      borderWidth: "1px",
                      lineHeight: "16px",
                    }}
                    label={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Download />
                        <Box sx={{ pl: "10px" }}>
                          <Typography
                            variant="small"
                            component="div"
                            sx={{ textAlign: "left" }}
                          >
                            {truncate(attachment.filename)}
                          </Typography>
                          <Typography
                            variant="small"
                            component="small"
                            sx={{ textAlign: "left" }}
                          >
                            {Math.round(
                              (attachment.content.byteLength / 1024) * 10
                            ) /
                              10 +
                              " KB"}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  ></Chip>
                </Tooltip>
              );
            else return <Fragment></Fragment>;
          })}
        </Box>
        <EmailBody />
        <a ref={downloadLinkRef} style={{ display: "none" }}></a>
      </Box>
    )
  );
}
